import { P2GGrid, useP2GMediaQueryOnly } from '@parcel2go/core-ui';
import { useCallback, useContext, useMemo } from 'react';
import useProfile from '~/apps/profile/use-profile';
import { SmartSendSubType } from '~/apps/smartsend/lib/consts/SmartSendSubType';
import { AchievementType } from '~/apps/smartsend/lib/context/ProfileContext';
import { SmartSendContext } from '~/apps/smartsend/lib/context/SmartSendContext';
import { onlyUnique } from '~/lib/utils/array';
import { ApplierOption } from '~/apps/smartsend/lib/helpers/ApplierOption';
import GenerateOptions from '~/apps/smartsend/lib/helpers/SmartSendBulkActionOptions';
import {
  OnSendReadyItemsAction,
  ToggleSelectionWithActionAction,
  UpdateFilterAction
} from '~/apps/smartsend/lib/context/SmartSendContextModels';
import { SmartSendItemsCache } from '~/apps/smartsend/lib/cache/SmartSendItemsCache';
import { BookEventType, Consts } from '~/apps/smartsend/lib/em/EmitTypes';
import { useEventEmitter } from '~/apps/smartsend/lib/em/Hooks';
import { getNamedFilter, getTabbedNamedFilter } from '~/apps/smartsend/lib/helpers/DefaultFilters';
import { SmartSendItemApiModel } from '~/lib/api/smartsend/types';
import ItemIsReady from '~/apps/smartsend/lib/helpers/ItemIsReady';
import { GetApplierOptionFromFilter } from '../smartsend/filter/GetApplierOptionsFromFilter';
import { ConcernSummaryUniqueKey } from '../concerns/convert-concerns-state-to-concerns';
import { ConcernsContext } from '~/apps/smartsend/lib/context/ConcernsContext';
import SmartAction from './smart-action';
import { ConcernType } from '../concerns/Concern';

const priority: ApplierOption[] = ['Service:Add', 'Dimensions:Add', 'RecipientContactDetails:Add'];

const GetOptionsForFixableItems = (items: SmartSendItemApiModel[]): ApplierOption[] =>
  items
    .filter((x) => x.State?.validation?.Errors?.length)
    .flatMap((i) => GenerateOptions(i, i.State.validation))
    .filter((x) => x.includes(':Add'));

const HasReadyItems = (items: SmartSendItemApiModel[]): boolean => items.some(ItemIsReady);

type props = {
  isXs: boolean;
  onAchievementClick: { [K in AchievementType]?: () => void };
  fix: () => void;
  send: () => void;
  wrapper: (children: React.ReactNode) => React.ReactNode;
  limitToConcernTypes?: ConcernType[];
  contained: boolean;
};

const SmartActions = ({ isXs, onAchievementClick, fix, send, wrapper, limitToConcernTypes }: props) => {
  const isSm = useP2GMediaQueryOnly('sm');
  const { data, hide, removeOutstandingActions } = useProfile();
  const { state, dispatch } = useContext(SmartSendContext);
  const { state: concernsState } = useContext(ConcernsContext);
  const { emit } = useEventEmitter<BookEventType>();
  const limit = useMemo(() => (isXs ? 1 : isSm ? 2 : 3), [isXs, isSm]);
  const currentFilter = useMemo(() => state.filters[SmartSendSubType].filter, [state]);
  const potentialFixes = useMemo(
    () => GetOptionsForFixableItems(SmartSendItemsCache),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [state]
  );
  const hasReadyItems = useMemo(
    () => HasReadyItems(SmartSendItemsCache),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [state]
  );
  const hasItems = useMemo(
    () => SmartSendItemsCache.some((item) => item.Status === 'Active' && item.MarketplaceStatus === 'Sold'),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [state]
  );
  const concerns = useMemo(
    () =>
      concernsState.Concerns.filter((x) => (limitToConcernTypes ? limitToConcernTypes.includes(x.Type) : true))
        .map(ConcernSummaryUniqueKey)
        .filter(onlyUnique).length,
    [concernsState, limitToConcernTypes]
  );

  const currentOption = useMemo(
    () => GetApplierOptionFromFilter(currentFilter.CurrentFilter),
    [currentFilter.CurrentFilter]
  );

  const onHide = (keys: AchievementType[]) => {
    hide(keys);
  };

  const onResetFilter = useCallback(() => {
    const updatedFilter = { ...getTabbedNamedFilter('DEFAULT', currentFilter.CurrentFilter) };
    updatedFilter.FilterSelections.filter((x) => x.Name === 'Issues').forEach((x) => {
      x.Include = [];
      x.Exclude = [];
    });
    dispatch(
      new UpdateFilterAction(SmartSendSubType, {
        ...state.filters[SmartSendSubType].filter,
        CurrentFilter: updatedFilter
      })
    );
  }, [currentFilter, dispatch, state.filters]);

  const onBulkFixItemsClick = useCallback(
    (a: ApplierOption) => {
      // Set the filter to limit to the specific issue.
      if (a) {
        const newFilter = getNamedFilter('Issues');
        newFilter.FilterSelections.forEach((f) => {
          if (f.Name === 'Issues') {
            f.Include = [a];
            f.Exclude = [];
          }
        });
        dispatch(
          new UpdateFilterAction(SmartSendSubType, {
            ...currentFilter,
            CurrentFilter: newFilter
          })
        );
      }

      // When the page has loaded, then trigger the 'Fix' screen.
      setTimeout(() => {
        fix();
      }, 500);
    },
    [currentFilter, dispatch, fix]
  );

  const onDoInBulk = useCallback(
    (a: ApplierOption) => {
      // Select all the items with the specific issue
      const options = SmartSendItemsCache.filter((i) => GenerateOptions(i, i.State.validation).includes(a)).map(
        (i) => i.Id
      );
      // Update the selection
      if (options.length > 0) {
        dispatch(new ToggleSelectionWithActionAction(options, true, a));
      }
    },
    [dispatch]
  );

  const onGenericFixItemsClick = useCallback(() => {
    // Select all the items with the specific issue
    const options = SmartSendItemsCache.filter((i) =>
      GenerateOptions(i, i.State.validation).some((a) => a.includes(':Add'))
    ).map((i) => i.Id);

    // Update the selection
    dispatch(new ToggleSelectionWithActionAction(options, true, 'LIST'));

    // When the page has loaded, then trigger the 'Fix' screen.
    setTimeout(() => {
      fix();
    }, 500);
  }, [dispatch, fix]);

  const onSend = useCallback(() => {
    // Switched The Tab
    send();

    // Set Filter
    dispatch(
      new OnSendReadyItemsAction(SmartSendSubType, {
        ...currentFilter,
        CurrentFilter: getNamedFilter('DEFAULT')
      })
    );

    // After the page has loaded, then trigger the 'Book' screen.
    setTimeout(() => {
      emit(Consts.Book, { Open: true, Ids: [] });
    }, 500);
  }, [dispatch, currentFilter, emit, send]);

  const contextualPrompts: { intent: string; key?: AchievementType }[] = useMemo(() => {
    if (data) {
      const output: { intent: string; key?: AchievementType }[] = [];

      if (hasReadyItems) {
        output.push({
          intent: 'READY'
        });
      }

      if (concerns) {
        output.push({ intent: 'CONCERNS' });
      }

      if (potentialFixes.length > 0) {
        const fixable = potentialFixes
          .filter((x) => x.includes(':Add'))
          .filter(onlyUnique)
          .sort((a, b) => {
            const pA = priority.indexOf(a);
            const pB = priority.indexOf(b);
            return pA - pB;
          });

        if (fixable.length) {
          fixable.forEach((x) => {
            output.push({ intent: x });
          });
        } else {
          output.push({ intent: 'FIX' });
        }
      }

      const done = data.achievements.filter((x) => x.value).map((x) => x.key);
      const notDone = data.achievements.filter((x) => !x.value && !x.hide).map((x) => x.key);

      // Linked account but not sent an order and not showing the "Ready" prompt
      if (
        hasItems &&
        !output.some((x) => x.intent === 'READY') &&
        done.includes('SmartSendSetupAccountScreen') &&
        notDone.includes('SmartSendActionFirstParcelSent')
      ) {
        output.push({
          ...data.achievements.find((x) => x.key === 'SmartSendActionFirstParcelSent'),
          intent: 'SEND_FIRST_PARCEL'
        });
      }

      // Sent and order but not linked an eCommerce account
      if (done.includes('SmartSendActionFirstParcelSent') && notDone.includes('SmartSendSetupAccountScreen')) {
        output.push({
          ...data.achievements.find((x) => x.key === 'SmartSendSetupAccountScreen'),
          intent: 'LINK_TOO'
        });

        return output.slice(0, limit);
      }

      // Not added an account
      const actionableLink = data.outstandingActions?.filter((x) => x !== 'csv') ?? [];
      const promptForAccountLinking = actionableLink.length || notDone.includes('SmartSendSetupAccountScreen');

      if (promptForAccountLinking) {
        // Eplicitly request the accounts we know about
        if (actionableLink.length) {
          actionableLink.forEach((key) => {
            output.push({
              intent: `LINK_${key}`
            });
          });
        } else {
          // Generic request to link an account
          output.push({
            ...data.achievements.find((x) => x.key === 'SmartSendSetupAccountScreen'),
            intent: 'LINK_ACCOUNT'
          });
        }
      }

      return output.slice(0, limit);
    }
  }, [data, hasReadyItems, concerns, potentialFixes, limit, hasItems]);

  if (!data || !contextualPrompts?.length) {
    return null;
  }

  return wrapper(
    <P2GGrid container justify="center" wrap={isXs ? 'wrap' : 'nowrap'} direction={isXs ? 'column' : 'row'} gap={2}>
      {contextualPrompts.map((contextualPrompt, i) => (
        <SmartAction
          prompts={contextualPrompts.length}
          onResetFilter={onResetFilter}
          key={contextualPrompt.intent}
          contextualPrompt={contextualPrompt}
          i={i}
          currentOption={currentOption}
          onHide={onHide}
          onAchievementClick={onAchievementClick}
          onSend={onSend}
          onGenericFixItemsClick={onGenericFixItemsClick}
          onBulkFixItemsClick={onBulkFixItemsClick}
          onBulkFixItemsBulkClick={onDoInBulk}
          removeOutstandingActions={removeOutstandingActions}
          hide={hide}
          limitToConcernTypes={limitToConcernTypes}
        />
      ))}
    </P2GGrid>
  );
};

export default SmartActions;
